/**
 * Loads the current user's publish entitlement read model. Follows the
 * useState/useEffect data pattern used elsewhere in this SPA (no react-query).
 *
 * `awaitCredit` — set when the user comes back from buying a credit. Checkout
 * reports "paid" at PAYMENT_COMPLETED, but the credit itself is granted by
 * the backend fulfillment sweep (runs every minute) — so the first read can
 * still say 0. While `canPublish` is false the hook re-checks silently
 * (`loading` stays false, no skeleton flicker), bounded by
 * CREDIT_POLL_MAX_ATTEMPTS so it never polls forever.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchPublishEntitlement } from './api';
import type { PublishEntitlementView } from './types';

export const CREDIT_POLL_INTERVAL_MS = 3000;
/** ~2 min: fulfillment sweep (≤60s) plus webhook/queue slack. */
export const CREDIT_POLL_MAX_ATTEMPTS = 40;

export interface UsePublishEntitlement {
  data: PublishEntitlementView | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** True while waiting (silently re-checking) for a just-purchased credit. */
  awaitingCredit: boolean;
  /** The wait ran out without a credit appearing — the grant may still land later. */
  creditWaitTimedOut: boolean;
}

/**
 * ⛔ **The backend currently IGNORES `templateId` and `invitationId` (F-03).**
 *
 * Intended, restored by the Phase 6 `InvitationEntitlement` cutover: purchased
 * credits are bound to the design they were bought for, so the wizard asks
 * about THIS design and omits both for the account-wide total (dashboard).
 *
 * Today every call returns the same account-wide view. A non-zero
 * `remainingCredits` here does **not** prove the user can publish this
 * particular design — the credit may have been bought for another one. Do not
 * render copy that promises design-scoped credits off the back of this hook.
 */
export function usePublishEntitlement(
  options: { awaitCredit?: boolean; templateId?: string; invitationId?: string; enabled?: boolean } = {},
): UsePublishEntitlement {
  // `enabled: false` skips the fetch (data stays null, loading false) so a caller that
  // only conditionally needs the value can still obey the Rules of Hooks.
  const { awaitCredit = false, templateId, invitationId, enabled = true } = options;
  const [data, setData] = useState<PublishEntitlementView | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchPublishEntitlement(templateId, invitationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load publishing rights.');
    } finally {
      setLoading(false);
    }
  }, [templateId, invitationId]);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const shouldPoll =
    awaitCredit && !loading && data !== null && !data.canPublish && pollAttempts < CREDIT_POLL_MAX_ATTEMPTS;

  // One timer per attempt: each tick bumps `pollAttempts`, which re-runs this
  // effect for the next tick; unmount (or the credit landing) clears it.
  useEffect(() => {
    if (!shouldPoll) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const next = await fetchPublishEntitlement(templateId, invitationId);
        if (!cancelled) setData(next);
      } catch {
        // Transient — the next tick retries; a background re-check never
        // replaces the page with an error.
      } finally {
        if (!cancelled) setPollAttempts((n) => n + 1);
      }
    }, CREDIT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shouldPoll, pollAttempts, templateId, invitationId]);

  const stillWithoutCredit = awaitCredit && data !== null && !data.canPublish;
  return {
    data,
    loading,
    error,
    reload,
    awaitingCredit: stillWithoutCredit && pollAttempts < CREDIT_POLL_MAX_ATTEMPTS,
    creditWaitTimedOut: stillWithoutCredit && pollAttempts >= CREDIT_POLL_MAX_ATTEMPTS,
  };
}
